import { useCallback } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { useSnackbar } from 'notistack';
import { isEqual } from 'lodash';

import { buildPath, ensureDefined } from '@e-socna/utils';
import { getDocumentName } from '@e-socna/data-layer';
import type { Id } from '@e-socna/data-layer';

import { userFriendlyErrors } from '../../../utils/back-errors.util';
import {
  PATH_EXTERNAL_ORGANIZATIONS,
  PATH_INTERNAL_ORGANIZATIONS,
  PATH_ORGANIZATION_GENERAL,
} from '../../../app/constants/paths.constant';
import { useViewSnackbarAction } from '../../../hooks/useViewSnackbarAction.hook';
import { useReferencesContext } from '../../../contexts/ReferencesContext/hooks/useReferencesContext.hook';
import { ThirdPartiesCmp_SomeOrganizations_QueryDocument } from '../../../components/ThirdParties/queries/some-organizations.query';
import { OrganizationPage_OneOrganization_QueryDocument } from '../../../pages/Organization/hooks/useFindOneOrganizationQuery.hook';
import type { BackError } from '../../../utils/back-errors.util';
import type { WizardSubmitHandlerParams } from '../../../components/Wizard/types/submit-handler.type';

import { memoForm2ApiMapper } from '../../MemoBeforeSubmit/mappers/memo.form2api.mapper';
import { FormVariant } from '../../enums/variant.enum';

import { organizationForm2ApiMapper } from '../mappers/organization.form2api.mapper';
import { getOrganizationIdInResult } from '../utils/get-organization-id-in-result.util';
import { organizationMembershipForm2ApiMapper } from '../mappers/organization-membership.form2api.mapper';
import type { ThirdPartyFormTab } from '../enums/tab.enum';
import type { ThirdPartyForm_Organization } from '../fragments/organization.fragment';
import type { OrganizationMutationResult } from '../types/organization-mutation-result.type';

import { useCreateOrganizationMutation } from './useCreateOrganizationMutation.hook';
import { useUpdateOrganizationMutation } from './useUpdateOrganizationMutation.hook';
import { useUpdateEmittersMutation } from './useUpdateEmittersMutation.hook';
import { useUpdateOrganizationMembershipsMutation } from './useUpdateOrganizationMembershipsMutation.hook';
import { useUpdateChildrenMutation } from './useUpdateChildrenMutation.hook';

interface Params {
  readonly formVariant?: FormVariant;
  readonly handleSuccess?: (result: OrganizationMutationResult) => void;
  readonly initialValues: ThirdPartyForm_Organization;
  readonly organizationId?: Id;
}

export const useOrganizationSubmit = ({
  formVariant,
  handleSuccess,
  initialValues,
  organizationId,
}: Readonly<Params>) => {
  const { genericStatusesCodeMap } = useReferencesContext();

  const { enqueueSnackbar } = useSnackbar();
  const { buildViewSnackbarAction } = useViewSnackbarAction();
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  const { mutate: createOrganizationMutation } =
    useCreateOrganizationMutation();
  const { mutate: updateOrganizationMutation } =
    useUpdateOrganizationMutation();
  const { mutate: updateEmittersMutation } = useUpdateEmittersMutation();
  const { mutate: updateOrganizationMembershipsMutation } =
    useUpdateOrganizationMembershipsMutation();
  const { mutate: updateChildrenMutation } = useUpdateChildrenMutation();

  const handleSubmit = useCallback(
    ({
      values,
      memos,
      formikHelpers: { setSubmitting },
      setWizardError,
    }: Readonly<
      WizardSubmitHandlerParams<ThirdPartyForm_Organization, ThirdPartyFormTab>
    >) => {
      setWizardError(undefined);

      const isCreation = !formVariant || formVariant === FormVariant.Creation;

      const mustUpdateChildren = isCreation
        ? values.childIds.length > 0
        : !isEqual(values.childIds, initialValues.childIds);

      const mustUpdateOrganizationMemberships = isCreation
        ? values.activeMemberships.length > 0
        : !isEqual(values.activeMemberships, initialValues.activeMemberships);

      const mustUpdateEmitters = isCreation
        ? values.billEmitterIds.length > 0
        : !isEqual(values.billEmitterIds, initialValues.billEmitterIds);

      const conclude = (result: OrganizationMutationResult) => {
        if (!formVariant || formVariant === FormVariant.Creation) {
          enqueueSnackbar('La nouvelle organisation a bien été créée.', {
            action: buildViewSnackbarAction(
              buildPath(PATH_ORGANIZATION_GENERAL, {
                id: getOrganizationIdInResult(result),
              }),
            ),
          });
          void queryClient.invalidateQueries({
            queryKey: [
              getDocumentName(ThirdPartiesCmp_SomeOrganizations_QueryDocument),
              {
                statusId: genericStatusesCodeMap.Enabled._id,
                isInternal: values.isInternal,
              },
            ],
          });
        } else {
          enqueueSnackbar("L'organisation a bien été mise à jour.");
          void queryClient.invalidateQueries({
            queryKey: [
              getDocumentName(ThirdPartiesCmp_SomeOrganizations_QueryDocument),
              {
                statusId: genericStatusesCodeMap.Enabled._id,
                isInternal: values.isInternal,
              },
            ],
          });
          void queryClient.invalidateQueries({
            queryKey: [
              getDocumentName(ThirdPartiesCmp_SomeOrganizations_QueryDocument),
              {
                statusId: genericStatusesCodeMap.Disabled._id,
                isInternal: values.isInternal,
              },
            ],
          });
          void queryClient.invalidateQueries({
            queryKey: [
              getDocumentName(OrganizationPage_OneOrganization_QueryDocument),
              {
                id: organizationId,
              },
            ],
          });
        }

        if (handleSuccess) {
          handleSuccess(result);
        } else if (!formVariant || formVariant === FormVariant.Creation) {
          void navigate(
            values.isInternal
              ? PATH_INTERNAL_ORGANIZATIONS
              : PATH_EXTERNAL_ORGANIZATIONS,
          );
        }

        setSubmitting(false);
      };

      const onError = (error: unknown) => {
        setWizardError({
          messages: userFriendlyErrors(error as BackError),
        });

        setSubmitting(false);
      };

      const onSuccess = (result: OrganizationMutationResult) => {
        if (
          !mustUpdateEmitters &&
          !mustUpdateOrganizationMemberships &&
          !mustUpdateChildren
        ) {
          conclude(result);
          return;
        }

        const organizationId = getOrganizationIdInResult(result);

        const promises = [];

        if (mustUpdateEmitters) {
          const emittersPromise = new Promise((resolve) => {
            const { organizationId: definedOrganizationId } = ensureDefined({
              organizationId,
            });

            updateEmittersMutation(
              {
                emitterIds: values.billEmitterIds,
                thirdPartyId: definedOrganizationId,
                versionData: memoForm2ApiMapper(memos),
              },
              {
                onError,
                onSuccess: resolve,
              },
            );
          });

          promises.push(emittersPromise);
        }

        if (mustUpdateOrganizationMemberships) {
          const membershipsPromise = new Promise((resolve) => {
            const { organizationId: definedOrganizationId } = ensureDefined({
              organizationId,
            });

            updateOrganizationMembershipsMutation(
              {
                activeMemberships: values.activeMemberships.map(
                  organizationMembershipForm2ApiMapper,
                ),
                organizationId: definedOrganizationId,
                ...memoForm2ApiMapper(memos),
              },
              {
                onError,
                onSuccess: resolve,
              },
            );
          });

          promises.push(membershipsPromise);
        }

        if (mustUpdateChildren) {
          const childrenPromise = new Promise((resolve) => {
            const { organizationId: definedOrganizationId } = ensureDefined({
              organizationId,
            });

            updateChildrenMutation(
              {
                childIds: values.childIds,
                organizationId: definedOrganizationId,
                versionData: memoForm2ApiMapper(memos),
              },
              {
                onError,
                onSuccess: resolve,
              },
            );
          });

          promises.push(childrenPromise);
        }

        void Promise.all(promises).then(() => {
          conclude(result);
        });
      };

      if (!formVariant || formVariant === FormVariant.Creation) {
        createOrganizationMutation(
          {
            organization: organizationForm2ApiMapper(values),
            versionData: memoForm2ApiMapper(memos),
          },
          {
            onError,
            onSuccess,
          },
        );
      } else if (organizationId) {
        updateOrganizationMutation(
          {
            filter: { _id: organizationId },
            update: organizationForm2ApiMapper(values),
            versionData: memoForm2ApiMapper(memos),
          },
          {
            onError,
            onSuccess,
          },
        );
      }

      return undefined;
    },
    [
      organizationId,
      buildViewSnackbarAction,
      createOrganizationMutation,
      enqueueSnackbar,
      formVariant,
      genericStatusesCodeMap.Disabled._id,
      genericStatusesCodeMap.Enabled._id,
      handleSuccess,
      initialValues.billEmitterIds,
      initialValues.childIds,
      initialValues.activeMemberships,
      navigate,
      queryClient,
      updateChildrenMutation,
      updateOrganizationMembershipsMutation,
      updateOrganizationMutation,
      updateEmittersMutation,
    ],
  );

  return { handleSubmit };
};
